import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit, faTrash, faSave, faSearch, faRefresh, faSpinner,
    faExclamationTriangle, faCheckCircle, faTimes, faChevronLeft,
    faChevronRight, faFilePdf, faCalendar, faUser, faStore, faPhone,
    faEnvelope, faMapMarkerAlt, faTag, faExchangeAlt, faChevronDown,
    faExpand, faCompress, faArrowsAlt, faMinus, faPlus, faFilter, faEye,
    faBox, faBarcode, faBuilding, faBalanceScale, faRupeeSign, faCalculator,
    faCalendarAlt, faWarehouse, faPercent, faBoxes, faLayerGroup, faCubes,
    faPills, faCoins, faGift, faMoneyBillWave, faClock, faExclamationCircle,
    faList, faTable, faThList, faFileAlt, faDownload, faPrint, faRecycle,
    faTrashAlt, faSkull
} from '@fortawesome/free-solid-svg-icons';

// ============================================
// HELPER FUNCTIONS
// ============================================

const getUnitDisplayLabel = (unitOfMeasure) => {
    const labels = {
        '1_liter': 'Liter', '1.5_liter': 'Liter', '2_liter': 'Liter',
        '3_liter': 'Liter', '5_liter': 'Liter',
        '200_ml': 'Milliliter', '250_ml': 'Milliliter', '300_ml': 'Milliliter',
        '350_ml': 'Milliliter', '400_ml': 'Milliliter', '450_ml': 'Milliliter',
        '500_ml': 'Milliliter',
        '0.5_liter': 'Liter', '0.7_liter': 'Liter', '1_liter_alt': 'Liter',
        '2_liter_alt': 'Liter', '3_liter_alt': 'Liter', '4_liter': 'Liter',
        '5_liter_alt': 'Liter', '6_liter': 'Liter', '7_liter': 'Liter',
        '10_liter': 'Liter', '20_liter': 'Liter', '60_liter': 'Liter',
        '208_liter': 'Liter', 'milliliter': 'Milliliter', 'kilogram': 'Kilogram',
        'gram': 'Gram', 'piece': 'Piece', 'dozen': 'Dozen', 'box': 'Box',
        'packet': 'Packet', 'can': 'Can', 'bottle': 'Bottle',
        'tablet': 'Tablet', 'capsule': 'Capsule', 'strip': 'Strip',
        'vial': 'Vial', 'ampoule': 'Ampoule', 'sachet': 'Sachet', 'tube': 'Tube'
    };
    return labels[unitOfMeasure] || 'Unit';
};

const getCategoryLabel = (category) => {
    const map = {
        'medicine': 'Medicine', 'surgical': 'Surgical', 'baby_care': 'Baby Care',
        'personal_care': 'Personal Care', 'veterinary': 'Veterinary', 'herbal': 'Herbal',
        'supplement': 'Supplement', 'medical_equipment': 'Medical Equipment',
        'diagnostic': 'Diagnostic', 'first_aid': 'First Aid', 'others': 'Others'
    };
    return map[category] || category || '-';
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    } catch { return dateString; }
};

const toMoney = (value) => {
    const num = parseFloat(value);
    return Number.isFinite(num) ? `Rs. ${num.toFixed(2)}` : '-';
};

// ============================================
// TOAST NOTIFICATION
// ============================================
const ToastNotification = ({ toast, setToast }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return 'Success';
            case 'error': return 'Error';
            case 'warning': return 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozlistui_toast-notification ${toast.type}`}>
            <div className="ozlistui_toast-content">
                <div className="ozlistui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozlistui_toast-text">
                    <div className="ozlistui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozlistui_toast-message">{toast.message}</div>
                </div>
                <button className="ozlistui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ============================================
// SHOP SELECTOR MODAL
// ============================================
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, loading }) => {
    if (!isOpen) return null;

    return (
        <div className="ozlistui-modal-overlay" onClick={onClose}>
            <div className="ozlistui-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="ozlistui-modal-header">
                    <h3>Switch Shop</h3>
                    <button className="ozlistui-close-btn" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="ozlistui-modal-body">
                    {loading ? (
                        <div className="ozlistui-loading-shops">
                            <div className="ozlistui-loading-spinner-small"></div>
                            <p>Loading shops...</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="ozlistui-no-shops">
                            <p>No shops available</p>
                        </div>
                    ) : (
                        <div className="ozlistui-shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`ozlistui-shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozlistui-active' : ''}`}
                                    onClick={() => onSelectShop(shop)}
                                >
                                    <div className="ozlistui-shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="ozlistui-shop-item-details">
                                        <div className="ozlistui-shop-item-name">{shop.shop_name}</div>
                                        <div className="ozlistui-shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="ozlistui-shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>Current</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================
// CUSTOM DROPDOWN COMPONENT
// ============================================
const CustomDropdown = ({ label, icon, value, onChange, options, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef(null);

    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption?.label || '';

    useEffect(() => {
        setSearchTerm(displayValue);
    }, [displayValue]);

    const filteredOptions = searchTerm.trim() === ''
        ? options
        : options.filter(opt =>
            opt.label.toLowerCase().includes(searchTerm.toLowerCase())
        );

    const handleSelect = (option) => {
        onChange(option.value);
        setSearchTerm(option.label);
        setIsOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="ozlistui-custom-dropdown-wrapper" ref={wrapperRef}>
            <div className="ozlistui-custom-input-wrapper">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIsOpen(true);
                        if (!options.find(opt => opt.label.toLowerCase() === e.target.value.toLowerCase())) {
                            onChange('');
                        }
                    }}
                    onFocus={() => setIsOpen(true)}
                    placeholder=" "
                    disabled={disabled}
                    autoComplete="off"
                />
                <label>
                    {icon && <FontAwesomeIcon icon={icon} />}
                    {label}
                </label>
                <FontAwesomeIcon icon={faChevronDown} className="ozlistui-custom-dropdown-arrow" />
            </div>

            {isOpen && (
                <div className="ozlistui-custom-dropdown-menu">
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map(opt => (
                            <div
                                key={opt.value}
                                className={`ozlistui-custom-dropdown-option ${value === opt.value ? 'ozlistui-selected' : ''}`}
                                onClick={() => handleSelect(opt)}
                            >
                                {opt.label}
                            </div>
                        ))
                    ) : (
                        <div className="ozlistui-custom-dropdown-empty">
                            No options found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ============================================
// VIEW DETAIL ROW
// ============================================
const DetailRow = ({ label, value }) => (
    <div className="ozlistui-view-row">
        <span className="ozlistui-view-label">{label}:</span>
        <span className="ozlistui-view-value">{value === '' || value === null || value === undefined ? '-' : value}</span>
    </div>
);

// ============================================
// DUMMY DATA
// ============================================
const DUMMY_DISPOSED_PRODUCTS = [
    {
        id: 1,
        product_name: 'Panadol Extra 500mg',
        product_code: 'PAN-001',
        batch_no: 'BATCH-2024-001',
        barcode: '8901234567890',
        category: 'medicine',
        company: 'GlaxoSmithKline',
        packing_type: 'Strip',
        sub_packing_type: 'Blister Pack',
        items_per_pack: 10,
        unit_of_measure: 'tablet',
        sell_per_pack: 120.00,
        total_quantity: 0,
        quantity_alert: 10,
        expiry_date: '2024-01-15',
        date: '2023-06-10',
        mrp: 150.00,
        trade_percent: 10,
        extra_percent: 0,
        bonus_buy: 0,
        bonus_free: 0,
        effective_cost_price: 90.00,
        effective_discount_percent: 10,
        mrp_per_pack_unit: 15.00,
        mrp_per_base_unit: 15.00,
        cost_price_per_pack_unit: 9.00,
        cost_price_per_base_unit: 9.00,
        pack_content_quantity: 10,
        pack_content_label: '10 Tablets',
        pack_base_unit_label: 'Tablet',
        sell_price_per_item_unit: 12.00,
        sell_price_per_base_unit: 12.00,
        bonus_stock_quantity: 0,
        actual_stock_quantity: 0,
        purchased_quantity: 50,
        username: 'admin',
        disposed_quantity: 50,
        disposal_date: '2024-01-20',
        disposal_reason: 'expired',
        disposed_by: 'admin'
    },
    {
        id: 2,
        product_name: 'Brufen 400mg',
        product_code: 'BRU-002',
        batch_no: 'BATCH-2024-002',
        barcode: '8901234567891',
        category: 'medicine',
        company: 'Abbott',
        packing_type: 'Strip',
        sub_packing_type: 'Blister Pack',
        items_per_pack: 10,
        unit_of_measure: 'tablet',
        sell_per_pack: 80.00,
        total_quantity: 0,
        quantity_alert: 15,
        expiry_date: '2024-02-28',
        date: '2023-07-15',
        mrp: 100.00,
        trade_percent: 15,
        extra_percent: 5,
        bonus_buy: 0,
        bonus_free: 0,
        effective_cost_price: 60.00,
        effective_discount_percent: 15,
        mrp_per_pack_unit: 10.00,
        mrp_per_base_unit: 10.00,
        cost_price_per_pack_unit: 6.00,
        cost_price_per_base_unit: 6.00,
        pack_content_quantity: 10,
        pack_content_label: '10 Tablets',
        pack_base_unit_label: 'Tablet',
        sell_price_per_item_unit: 8.00,
        sell_price_per_base_unit: 8.00,
        bonus_stock_quantity: 0,
        actual_stock_quantity: 0,
        purchased_quantity: 30,
        username: 'pharmacist',
        disposed_quantity: 30,
        disposal_date: '2024-03-01',
        disposal_reason: 'damaged',
        disposed_by: 'pharmacist'
    },
    {
        id: 3,
        product_name: 'Augmentin 625mg',
        product_code: 'AUG-003',
        batch_no: 'BATCH-2023-015',
        barcode: '8901234567892',
        category: 'medicine',
        company: 'GlaxoSmithKline',
        packing_type: 'Strip',
        sub_packing_type: 'Blister Pack',
        items_per_pack: 6,
        unit_of_measure: 'tablet',
        sell_per_pack: 250.00,
        total_quantity: 0,
        quantity_alert: 5,
        expiry_date: '2023-12-10',
        date: '2023-05-20',
        mrp: 300.00,
        trade_percent: 12,
        extra_percent: 0,
        bonus_buy: 0,
        bonus_free: 0,
        effective_cost_price: 180.00,
        effective_discount_percent: 12,
        mrp_per_pack_unit: 50.00,
        mrp_per_base_unit: 50.00,
        cost_price_per_pack_unit: 30.00,
        cost_price_per_base_unit: 30.00,
        pack_content_quantity: 6,
        pack_content_label: '6 Tablets',
        pack_base_unit_label: 'Tablet',
        sell_price_per_item_unit: 41.67,
        sell_price_per_base_unit: 41.67,
        bonus_stock_quantity: 0,
        actual_stock_quantity: 0,
        purchased_quantity: 20,
        username: 'admin',
        disposed_quantity: 20,
        disposal_date: '2023-12-15',
        disposal_reason: 'recalled',
        disposed_by: 'admin'
    },
    {
        id: 4,
        product_name: 'Vitamin C 500mg',
        product_code: 'VIT-004',
        batch_no: 'BATCH-2024-008',
        barcode: '8901234567893',
        category: 'supplement',
        company: 'Herbalife',
        packing_type: 'Bottle',
        sub_packing_type: 'Plastic Bottle',
        items_per_pack: 30,
        unit_of_measure: 'tablet',
        sell_per_pack: 450.00,
        total_quantity: 0,
        quantity_alert: 20,
        expiry_date: '2024-03-20',
        date: '2023-08-10',
        mrp: 500.00,
        trade_percent: 8,
        extra_percent: 2,
        bonus_buy: 0,
        bonus_free: 0,
        effective_cost_price: 350.00,
        effective_discount_percent: 8,
        mrp_per_pack_unit: 16.67,
        mrp_per_base_unit: 16.67,
        cost_price_per_pack_unit: 11.67,
        cost_price_per_base_unit: 11.67,
        pack_content_quantity: 30,
        pack_content_label: '30 Tablets',
        pack_base_unit_label: 'Tablet',
        sell_price_per_item_unit: 15.00,
        sell_price_per_base_unit: 15.00,
        bonus_stock_quantity: 0,
        actual_stock_quantity: 0,
        purchased_quantity: 45,
        username: 'pharmacist',
        disposed_quantity: 45,
        disposal_date: '2024-03-25',
        disposal_reason: 'near_expiry',
        disposed_by: 'pharmacist'
    },
    {
        id: 5,
        product_name: 'Dettol Antiseptic Liquid',
        product_code: 'DET-005',
        batch_no: 'BATCH-2023-022',
        barcode: '8901234567894',
        category: 'personal_care',
        company: 'Reckitt Benckiser',
        packing_type: 'Bottle',
        sub_packing_type: 'Plastic Bottle',
        items_per_pack: 1,
        unit_of_measure: 'liter',
        sell_per_pack: 180.00,
        total_quantity: 0,
        quantity_alert: 10,
        expiry_date: '2023-11-30',
        date: '2023-04-15',
        mrp: 200.00,
        trade_percent: 5,
        extra_percent: 0,
        bonus_buy: 0,
        bonus_free: 0,
        effective_cost_price: 150.00,
        effective_discount_percent: 5,
        mrp_per_pack_unit: 200.00,
        mrp_per_base_unit: 200.00,
        cost_price_per_pack_unit: 150.00,
        cost_price_per_base_unit: 150.00,
        pack_content_quantity: 1,
        pack_content_label: '1 Liter',
        pack_base_unit_label: 'Liter',
        sell_price_per_item_unit: 180.00,
        sell_price_per_base_unit: 180.00,
        bonus_stock_quantity: 0,
        actual_stock_quantity: 0,
        purchased_quantity: 15,
        username: 'admin',
        disposed_quantity: 15,
        disposal_date: '2023-12-05',
        disposal_reason: 'stock_clearance',
        disposed_by: 'admin'
    },
    {
        id: 6,
        product_name: 'Betadine Ointment',
        product_code: 'BET-006',
        batch_no: 'BATCH-2023-030',
        barcode: '8901234567895',
        category: 'medicine',
        company: 'Winthrop',
        packing_type: 'Tube',
        sub_packing_type: 'Aluminium Tube',
        items_per_pack: 1,
        unit_of_measure: 'gram',
        sell_per_pack: 95.00,
        total_quantity: 0,
        quantity_alert: 10,
        expiry_date: '2023-10-15',
        date: '2023-03-01',
        mrp: 110.00,
        trade_percent: 10,
        extra_percent: 0,
        bonus_buy: 0,
        bonus_free: 0,
        effective_cost_price: 70.00,
        effective_discount_percent: 10,
        mrp_per_pack_unit: 110.00,
        mrp_per_base_unit: 110.00,
        cost_price_per_pack_unit: 70.00,
        cost_price_per_base_unit: 70.00,
        pack_content_quantity: 1,
        pack_content_label: '15g Tube',
        pack_base_unit_label: 'Gram',
        sell_price_per_item_unit: 95.00,
        sell_price_per_base_unit: 95.00,
        bonus_stock_quantity: 0,
        actual_stock_quantity: 0,
        purchased_quantity: 25,
        username: 'pharmacist',
        disposed_quantity: 25,
        disposal_date: '2023-10-20',
        disposal_reason: 'others',
        disposed_by: 'pharmacist'
    }
];

const DUMMY_SHOPS = [
    { shop_id: 1, shop_name: 'City Pharmacy', shop_code: 'CP-001' },
    { shop_id: 2, shop_name: 'Health Plus', shop_code: 'HP-002' },
    { shop_id: 3, shop_name: 'MediCare Store', shop_code: 'MC-003' }
];

// ============================================
// MAIN DISPOSED PRODUCT LIST COMPONENT
// ============================================
const PharmacyDisposedProducts = () => {
    const { user, currentShop, switchShop } = useAuth();
    const [disposedProducts, setDisposedProducts] = useState(DUMMY_DISPOSED_PRODUCTS);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [fetchError, setFetchError] = useState('');

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState(DUMMY_SHOPS);
    const [loadingShops, setLoadingShops] = useState(false);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        progress: 100,
        timeLeft: 3
    });

    // Edit form state
    const [editFormData, setEditFormData] = useState({
        product_name: '',
        product_code: '',
        batch_no: '',
        barcode: '',
        category: '',
        company: '',
        packing_type: '',
        sub_packing_type: '',
        items_per_pack: '',
        unit_of_measure: '',
        sell_per_pack: '',
        total_quantity: '',
        quantity_alert: '',
        expiry_date: '',
        date: '',
        mrp: '',
        trade_percent: '',
        extra_percent: '',
        bonus_buy: '',
        bonus_free: '',
        effective_cost_price: '',
        effective_discount_percent: '',
        mrp_per_pack_unit: '',
        mrp_per_base_unit: '',
        cost_price_per_pack_unit: '',
        cost_price_per_base_unit: '',
        pack_content_quantity: '',
        pack_content_label: '',
        pack_base_unit_label: '',
        sell_price_per_item_unit: '',
        sell_price_per_base_unit: '',
        bonus_stock_quantity: '',
        actual_stock_quantity: '',
        purchased_quantity: '',
        disposed_quantity: '',
        disposal_date: '',
        disposal_reason: '',
        disposed_by: ''
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Filter states
    const [filterBy, setFilterBy] = useState('');
    const [productNameFilter, setProductNameFilter] = useState('');
    const [productCodeFilter, setProductCodeFilter] = useState('');
    const [batchFilter, setBatchFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [disposalReasonFilter, setDisposalReasonFilter] = useState('');

    const toastTimerRef = useRef(null);
    const isMountedRef = useRef(true);

    // Options
    const categoryOptions = useMemo(() => [
        { value: '', label: 'All Categories' },
        { value: 'medicine', label: 'Medicine' },
        { value: 'surgical', label: 'Surgical' },
        { value: 'baby_care', label: 'Baby Care' },
        { value: 'personal_care', label: 'Personal Care' },
        { value: 'veterinary', label: 'Veterinary' },
        { value: 'herbal', label: 'Herbal' },
        { value: 'supplement', label: 'Supplement' },
        { value: 'medical_equipment', label: 'Medical Equipment' },
        { value: 'diagnostic', label: 'Diagnostic' },
        { value: 'first_aid', label: 'First Aid' },
        { value: 'others', label: 'Others' }
    ], []);

    const disposalReasonOptions = useMemo(() => [
        { value: '', label: 'All Reasons' },
        { value: 'expired', label: 'Expired' },
        { value: 'damaged', label: 'Damaged' },
        { value: 'recalled', label: 'Recalled' },
        { value: 'near_expiry', label: 'Near Expiry' },
        { value: 'stock_clearance', label: 'Stock Clearance' },
        { value: 'others', label: 'Others' }
    ], []);

    const filterByOptions = useMemo(() => [
        { value: 'all', label: 'All' },
        { value: 'productName', label: 'Product Name' },
        { value: 'productCode', label: 'Product Code' },
        { value: 'batch', label: 'Batch Number' },
        { value: 'category', label: 'Category' },
        { value: 'company', label: 'Company' },
        { value: 'date', label: 'Date Range' },
        { value: 'added_by', label: 'Added By' }
    ], []);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        setToast({ show: true, type, message, progress: 100, timeLeft: 3 });
        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;
            setToast(prev => ({ ...prev, progress, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);
        toastTimerRef.current = timer;
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        };
    }, []);

    // ============================================
    // RESET FILTERS
    // ============================================
    const resetFilters = useCallback(() => {
        setFilterBy('');
        setProductNameFilter('');
        setProductCodeFilter('');
        setBatchFilter('');
        setCategoryFilter('');
        setCompanyFilter('');
        setAddedByFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
        setDisposalReasonFilter('');
    }, []);

    // ============================================
    // FETCH DISPOSED PRODUCTS (DUMMY)
    // ============================================
    const fetchDisposedProducts = useCallback(() => {
        setLoading(true);
        setFetchError('');
        
        // Simulate API delay
        setTimeout(() => {
            setDisposedProducts(DUMMY_DISPOSED_PRODUCTS);
            setLoading(false);
            showToast('success', 'Disposed products loaded successfully');
        }, 500);
    }, [showToast]);

    // ============================================
    // HANDLE DELETE (DUMMY)
    // ============================================
    const handleDelete = useCallback(() => {
        if (!productToDelete) return;
        
        setLoading(true);
        setTimeout(() => {
            const updatedProducts = disposedProducts.filter(p => p.id !== productToDelete);
            setDisposedProducts(updatedProducts);
            setShowDeleteModal(false);
            setProductToDelete(null);
            setLoading(false);
            showToast('success', 'Product deleted successfully');
        }, 500);
    }, [productToDelete, disposedProducts, showToast]);

    // ============================================
    // HANDLE UPDATE (DUMMY)
    // ============================================
    const handleUpdateProduct = useCallback((e) => {
        e.preventDefault();
        
        const errors = {};
        if (!editFormData.product_name.trim()) errors.product_name = 'Product name is required';
        if (!editFormData.batch_no.trim()) errors.batch_no = 'Batch number is required';
        if (!editFormData.items_per_pack || parseFloat(editFormData.items_per_pack) <= 0) errors.items_per_pack = 'Items per pack is required';
        if (!editFormData.unit_of_measure) errors.unit_of_measure = 'Unit of measure is required';
        if (!editFormData.sell_per_pack || parseFloat(editFormData.sell_per_pack) <= 0) errors.sell_per_pack = 'Retail price is required';
        if (!editFormData.total_quantity || parseFloat(editFormData.total_quantity) < 0) errors.total_quantity = 'Total quantity is required';
        if (!editFormData.quantity_alert || parseFloat(editFormData.quantity_alert) < 0) errors.quantity_alert = 'Low stock alert is required';
        
        if (Object.keys(errors).length > 0) { 
            setEditFormErrors(errors); 
            return; 
        }
        
        setEditLoading(true);
        setTimeout(() => {
            const updatedProducts = disposedProducts.map(product => 
                product.id === selectedProduct.id 
                    ? { ...product, ...editFormData }
                    : product
            );
            setDisposedProducts(updatedProducts);
            setShowEditModal(false);
            setEditLoading(false);
            showToast('success', 'Product updated successfully');
        }, 500);
    }, [selectedProduct, editFormData, disposedProducts, showToast]);

    // ============================================
    // FILTERING
    // ============================================
    const applyFilters = useCallback(() => {
        let filtered = [...disposedProducts];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(product =>
                product.product_name?.toLowerCase().includes(term) ||
                product.product_code?.toLowerCase().includes(term) ||
                product.batch_no?.toLowerCase().includes(term) ||
                product.barcode?.toLowerCase().includes(term) ||
                product.company?.toLowerCase().includes(term) ||
                product.category?.toLowerCase().includes(term) ||
                product.username?.toLowerCase().includes(term) ||
                product.disposal_reason?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'productName' && productNameFilter.trim()) {
            const term = productNameFilter.toLowerCase();
            filtered = filtered.filter(product => product.product_name?.toLowerCase().includes(term));
        }
        if (filterBy === 'productCode' && productCodeFilter.trim()) {
            const term = productCodeFilter.toLowerCase();
            filtered = filtered.filter(product => product.product_code?.toLowerCase().includes(term));
        }
        if (filterBy === 'batch' && batchFilter.trim()) {
            const term = batchFilter.toLowerCase();
            filtered = filtered.filter(product => product.batch_no?.toLowerCase().includes(term));
        }
        if (filterBy === 'category' && categoryFilter) {
            filtered = filtered.filter(product => product.category === categoryFilter);
        }
        if (filterBy === 'company' && companyFilter.trim()) {
            const term = companyFilter.toLowerCase();
            filtered = filtered.filter(product => product.company?.toLowerCase().includes(term));
        }
        if (filterBy === 'added_by' && addedByFilter.trim()) {
            const term = addedByFilter.toLowerCase();
            filtered = filtered.filter(product => product.username?.toLowerCase().includes(term));
        }

        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(product => {
                if (!product.disposal_date) return false;
                const productDate = new Date(product.disposal_date);
                productDate.setHours(0, 0, 0, 0);
                return productDate >= start;
            });
        }
        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(product => {
                if (!product.disposal_date) return false;
                const productDate = new Date(product.disposal_date);
                return productDate <= end;
            });
        }

        if (disposalReasonFilter) {
            filtered = filtered.filter(product => product.disposal_reason === disposalReasonFilter);
        }

        setFilteredProducts(filtered);
        setCurrentPage(1);
    }, [disposedProducts, searchTerm, filterBy, productNameFilter, productCodeFilter, batchFilter, categoryFilter, companyFilter, addedByFilter, startDate, endDate, disposalReasonFilter]);

    useEffect(() => { applyFilters(); }, [applyFilters]);

    // ============================================
    // PAGINATION
    // ============================================
    const pagination = useMemo(() => {
        const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const currentProducts = filteredProducts.slice(startIndex, endIndex);
        return { totalPages, startIndex, endIndex, currentProducts };
    }, [filteredProducts, currentPage, itemsPerPage]);

    // ============================================
    // HANDLE ACTIONS
    // ============================================
    const handleView = useCallback((product) => { 
        setSelectedProduct(product); 
        setShowViewModal(true); 
    }, []);

    const handleEdit = useCallback((product) => {
        setSelectedProduct(product);
        setEditFormData({
            product_name: product.product_name || '',
            product_code: product.product_code || '',
            batch_no: product.batch_no || '',
            barcode: product.barcode || '',
            category: product.category || '',
            company: product.company || '',
            packing_type: product.packing_type || '',
            sub_packing_type: product.sub_packing_type || '',
            items_per_pack: product.items_per_pack || '',
            unit_of_measure: product.unit_of_measure || '',
            sell_per_pack: product.sell_per_pack || '',
            total_quantity: product.total_quantity || '',
            quantity_alert: product.quantity_alert || '',
            expiry_date: product.expiry_date || '',
            date: product.date || '',
            mrp: product.mrp || '',
            trade_percent: product.trade_percent || '',
            extra_percent: product.extra_percent || '',
            bonus_buy: product.bonus_buy || '',
            bonus_free: product.bonus_free || '',
            effective_cost_price: product.effective_cost_price || '',
            effective_discount_percent: product.effective_discount_percent || '',
            mrp_per_pack_unit: product.mrp_per_pack_unit || '',
            mrp_per_base_unit: product.mrp_per_base_unit || '',
            cost_price_per_pack_unit: product.cost_price_per_pack_unit || '',
            cost_price_per_base_unit: product.cost_price_per_base_unit || '',
            pack_content_quantity: product.pack_content_quantity || '',
            pack_content_label: product.pack_content_label || '',
            pack_base_unit_label: product.pack_base_unit_label || '',
            sell_price_per_item_unit: product.sell_price_per_item_unit || '',
            sell_price_per_base_unit: product.sell_price_per_base_unit || '',
            bonus_stock_quantity: product.bonus_stock_quantity || '',
            actual_stock_quantity: product.actual_stock_quantity || '',
            purchased_quantity: product.purchased_quantity || '',
            disposed_quantity: product.disposed_quantity || '',
            disposal_date: product.disposal_date || '',
            disposal_reason: product.disposal_reason || '',
            disposed_by: product.disposed_by || ''
        });
        setEditFormErrors({});
        setShowEditModal(true);
    }, []);

    const handleDeleteClick = useCallback((productId) => {
        const product = disposedProducts.find(p => p.id === productId);
        if (!product) return;
        setProductToDelete(productId);
        setShowDeleteModal(true);
    }, [disposedProducts]);

    const handlePageChange = useCallback((page) => { 
        if (page >= 1 && page <= pagination.totalPages) setCurrentPage(page); 
    }, [pagination.totalPages]);

    const handleSwitchShop = useCallback(async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', 'Shop switched successfully');
                setCurrentPage(1);
                resetFilters();
                setShowShopSelector(false);
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    }, [switchShop, showToast, resetFilters]);

    // ============================================
    // RENDER HELPERS
    // ============================================

    const renderStickyHeader = () => (
        <div className="ozlistui-sticky-header">
            <div className="ozlistui-header-bg-animation">
                <div className="gradient-orb orb-1"></div>
                <div className="gradient-orb orb-2"></div>
                <div className="gradient-orb orb-3"></div>
                <div className="gradient-orb orb-4"></div>
                <div className="gradient-orb orb-5"></div>
            </div>
            <div className="ozlistui-header-content">
                <div className="ozlistui-header-left">
                    <div className="ozlistui-header-title">
                        <h1>
                            <FontAwesomeIcon icon={faRecycle} style={{ color: '#6b7280', marginRight: '10px' }} />
                            Disposed Products List
                        </h1>
                        <p>
                            Products that have been disposed
                            <span className="ozlistui-disposed-badge" style={{ 
                                backgroundColor: '#6b7280', 
                                color: 'white', 
                                padding: '4px 12px', 
                                borderRadius: '20px', 
                                marginLeft: '10px',
                                fontSize: '12px',
                                fontWeight: 'bold'
                            }}>
                                {disposedProducts.length} Disposed
                            </span>
                        </p>
                    </div>
                </div>
                <div className="ozlistui-header-right">
                    <button
                        className="ozlistui-header-filter-btn"
                        onClick={() => setShowFilters(!showFilters)}
                        title={showFilters ? "Hide Filters" : "Show Filters"}
                    >
                        <FontAwesomeIcon icon={faFilter} />
                        <span>{showFilters ? "Hide Filters" : "Filters"}</span>
                    </button>
                    {!currentShop && (
                        <button className="ozlistui-header-shop-btn" onClick={() => setShowShopSelector(true)}>
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>Switch Shop</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    const renderFiltersSection = () => (
        <div className="ozlistui-filters-section">
            <div className="ozlistui-filters-row">
                <CustomDropdown
                    label="Filter By"
                    icon={faTag}
                    value={filterBy}
                    onChange={setFilterBy}
                    options={filterByOptions}
                />

                {filterBy === 'productName' && (
                    <div className="ozlistui-filter-group">
                        <div className="ozlistui-floating-wrapper">
                            <input
                                type="text"
                                id="productNameFilter"
                                value={productNameFilter}
                                onChange={(e) => setProductNameFilter(e.target.value)}
                                placeholder=" "
                            />
                            <label htmlFor="productNameFilter">
                                <FontAwesomeIcon icon={faBox} />
                                Product Name
                            </label>
                        </div>
                    </div>
                )}

                {filterBy === 'productCode' && (
                    <div className="ozlistui-filter-group">
                        <div className="ozlistui-floating-wrapper">
                            <input
                                type="text"
                                id="productCodeFilter"
                                value={productCodeFilter}
                                onChange={(e) => setProductCodeFilter(e.target.value)}
                                placeholder=" "
                            />
                            <label htmlFor="productCodeFilter">
                                <FontAwesomeIcon icon={faTag} />
                                Product Code
                            </label>
                        </div>
                    </div>
                )}

                {filterBy === 'batch' && (
                    <div className="ozlistui-filter-group">
                        <div className="ozlistui-floating-wrapper">
                            <input
                                type="text"
                                id="batchFilter"
                                value={batchFilter}
                                onChange={(e) => setBatchFilter(e.target.value)}
                                placeholder=" "
                            />
                            <label htmlFor="batchFilter">
                                <FontAwesomeIcon icon={faTag} />
                                Batch Number
                            </label>
                        </div>
                    </div>
                )}

                {filterBy === 'category' && (
                    <CustomDropdown
                        label="Category"
                        icon={faTag}
                        value={categoryFilter}
                        onChange={setCategoryFilter}
                        options={categoryOptions}
                    />
                )}

                {filterBy === 'company' && (
                    <div className="ozlistui-filter-group">
                        <div className="ozlistui-floating-wrapper">
                            <input
                                type="text"
                                id="companyFilter"
                                value={companyFilter}
                                onChange={(e) => setCompanyFilter(e.target.value)}
                                placeholder=" "
                            />
                            <label htmlFor="companyFilter">
                                <FontAwesomeIcon icon={faBuilding} />
                                Company
                            </label>
                        </div>
                    </div>
                )}

                {filterBy === 'date' && (
                    <>
                        <div className="ozlistui-filter-group ozlistui-date-filter">
                            <div className="ozlistui-floating-wrapper">
                                <input
                                    type="date"
                                    id="startDate"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    max={new Date().toISOString().split('T')[0]}
                                    placeholder=" "
                                />
                                <label htmlFor="startDate">
                                    <FontAwesomeIcon icon={faCalendar} />
                                    Start Date
                                </label>
                            </div>
                        </div>
                        <div className="ozlistui-filter-group ozlistui-date-filter">
                            <div className="ozlistui-floating-wrapper">
                                <input
                                    type="date"
                                    id="endDate"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={startDate}
                                    max={new Date().toISOString().split('T')[0]}
                                    placeholder=" "
                                />
                                <label htmlFor="endDate">
                                    <FontAwesomeIcon icon={faCalendar} />
                                    End Date
                                </label>
                            </div>
                        </div>
                    </>
                )}

                {filterBy === 'added_by' && (
                    <div className="ozlistui-filter-group">
                        <div className="ozlistui-floating-wrapper">
                            <input
                                type="text"
                                id="addedByFilter"
                                value={addedByFilter}
                                onChange={(e) => setAddedByFilter(e.target.value)}
                                placeholder=" "
                            />
                            <label htmlFor="addedByFilter">
                                <FontAwesomeIcon icon={faUser} />
                                Added By
                            </label>
                        </div>
                    </div>
                )}

                <CustomDropdown
                    label="Disposal Reason"
                    icon={faTrashAlt}
                    value={disposalReasonFilter}
                    onChange={setDisposalReasonFilter}
                    options={disposalReasonOptions}
                />

                <div className="ozlistui-filter-group">
                    <button onClick={fetchDisposedProducts} className="ozlistui-refresh-btn" disabled={loading}>
                        <FontAwesomeIcon icon={faRefresh} spin={loading} />
                        <span className="ozlistui-btn-text">Refresh</span>
                    </button>
                </div>
            </div>
        </div>
    );

    const renderTableHead = () => (
        <thead>
            <tr>
                <th className="ozlistui-id-column ozlistui-centered-cell" style={{ minWidth: '40px' }}>#</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '150px' }}>Product Name</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '90px' }}>Batch</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '80px' }}>Unit</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '90px' }}>MRP</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '90px' }}>Retail</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '100px' }}>Category</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '80px' }}>Disposed Qty</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '100px' }}>Disposal Date</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '120px' }}>Reason</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '100px' }}>Disposed By</th>
                <th className="ozlistui-actions-column ozlistui-centered-cell" style={{ minWidth: '120px' }}>Actions</th>
            </tr>
        </thead>
    );

    const renderTableRow = (product, index) => {
        const isSelfAdded = product.username === user?.username;

        return (
            <tr key={product.id} className="ozlistui-disposed-row" style={{ backgroundColor: '#f3f4f6' }}>
                <td className="ozlistui-id-cell ozlistui-centered-cell">
                    <span className="ozlistui-cell-text">{pagination.startIndex + index + 1}</span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span className="ozlistui-cell-text ozlistui-product-name">{product.product_name || 'Nil'}</span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span className="ozlistui-cell-text">{product.batch_no || 'Nil'}</span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span className="ozlistui-cell-text">{getUnitDisplayLabel(product.unit_of_measure) || 'Nil'}</span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span className="ozlistui-cell-text">{toMoney(product.mrp)}</span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span className="ozlistui-cell-text">{toMoney(product.sell_per_pack)}</span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span className="ozlistui-cell-text">{getCategoryLabel(product.category)}</span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span className="ozlistui-cell-text ozlistui-disposed-qty" style={{ fontWeight: 'bold', color: '#6b7280' }}>
                        {product.disposed_quantity || '0'}
                    </span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span className="ozlistui-cell-text">{formatDate(product.disposal_date)}</span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span className="ozlistui-disposal-reason-badge" style={{ 
                        backgroundColor: '#e5e7eb', 
                        color: '#374151',
                        padding: '2px 10px',
                        borderRadius: '12px',
                        fontWeight: '500',
                        fontSize: '11px',
                        display: 'inline-block'
                    }}>
                        {product.disposal_reason || '—'}
                    </span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span className={`ozlistui-added-by-badge ${isSelfAdded ? 'ozlistui-self' : ''}`}>
                        <span className="ozlistui-cell-text">{product.disposed_by || product.username || '-'}</span>
                    </span>
                </td>
                <td className="ozlistui-actions-cell ozlistui-centered-cell">
                    <div className="ozlistui-actions-content">
                        <button onClick={() => handleView(product)} className="ozlistui-action-btn ozlistui-view-btn" title="View">
                            <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button onClick={() => handleEdit(product)} className="ozlistui-action-btn ozlistui-edit-btn" title="Edit">
                            <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button onClick={() => handleDeleteClick(product.id)} className="ozlistui-action-btn ozlistui-delete-btn" title="Delete" disabled={!isSelfAdded} style={{ opacity: isSelfAdded ? 1 : 0.5, cursor: isSelfAdded ? 'pointer' : 'not-allowed' }}>
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    </div>
                </td>
            </tr>
        );
    };

    const renderTable = () => (
        <div className="ozlistui-table-scroll">
            <div className="ozlistui-table-container">
                <div className="ozlistui-table-wrapper">
                    <table className="ozlistui-table">
                        {renderTableHead()}
                        <tbody>
                            {pagination.currentProducts.length === 0 ? (
                                <tr>
                                    <td colSpan={12} style={{ textAlign: 'center', padding: '2rem' }}>
                                        {fetchError
                                            ? `Error: ${fetchError}`
                                            : 'No disposed products found for this shop.'}
                                    </td>
                                </tr>
                            ) : (
                                pagination.currentProducts.map((product, index) => renderTableRow(product, index))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const renderPaginationControls = () => {
        if (filteredProducts.length === 0 || !currentShop) return null;
        return (
            <div className="ozlistui-pagination">
                <div className="ozlistui-pagination-info">
                    <span>Showing {pagination.startIndex + 1} of {filteredProducts.length} products</span>
                </div>
                <div className="ozlistui-pagination-controls">
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="ozlistui-pagination-btn">
                        <FontAwesomeIcon icon={faChevronLeft} />
                        <span>Previous</span>
                    </button>
                    <div className="ozlistui-page-numbers">
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                            let pageNum;
                            if (pagination.totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (currentPage <= 3) {
                                pageNum = i + 1;
                            } else if (currentPage >= pagination.totalPages - 2) {
                                pageNum = pagination.totalPages - 4 + i;
                            } else {
                                pageNum = currentPage - 2 + i;
                            }
                            return (
                                <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`ozlistui-page-number ${currentPage === pageNum ? 'ozlistui-active' : ''}`}>
                                    {pageNum}
                                </button>
                            );
                        })}
                    </div>
                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.totalPages} className="ozlistui-pagination-btn">
                        <span>Next</span>
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            </div>
        );
    };

    // ============================================
    // VIEW MODAL - Full Product Details
    // ============================================
    const renderViewModal = () => {
        if (!showViewModal || !selectedProduct) return null;
        const p = selectedProduct;

        return (
            <div className="ozlistui-modal-overlay" onClick={(e) => e.stopPropagation()}>
                <div className="ozlistui-modal-content ozlistui-view-modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="ozlistui-modal-header" style={{ borderBottomColor: '#6b7280' }}>
                        <div className="ozlistui-modal-icon" style={{ backgroundColor: '#6b7280' }}>
                            <FontAwesomeIcon icon={faRecycle} />
                        </div>
                        <div className="ozlistui-modal-title">
                            <h3>Product Details</h3>
                            <span className="ozlistui-product-number-badge">#{p.id}</span>
                        </div>
                        <button className="ozlistui-close-btn" onClick={() => setShowViewModal(false)}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                    <div className="ozlistui-modal-body ozlistui-view-modal-body">
                        {/* Disposal Information - Highlighted */}
                        <div className="ozlistui-view-section" style={{ backgroundColor: '#f3f4f6', border: '2px solid #6b7280', borderRadius: '8px', padding: '15px', marginBottom: '15px' }}>
                            <h4 className="ozlistui-view-section-title" style={{ color: '#4b5563' }}>
                                <FontAwesomeIcon icon={faTrashAlt} /> Disposal Information
                            </h4>
                            <div className="ozlistui-view-grid">
                                <DetailRow label="Disposed Quantity" value={p.disposed_quantity || '0'} />
                                <DetailRow label="Disposal Date" value={formatDate(p.disposal_date)} />
                                <DetailRow label="Disposal Reason" value={p.disposal_reason || '-'} />
                                <DetailRow label="Disposed By" value={p.disposed_by || p.username || '-'} />
                            </div>
                        </div>

                        {/* Product Information */}
                        <div className="ozlistui-view-section">
                            <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faBox} />Product Information</h4>
                            <div className="ozlistui-view-grid">
                                <DetailRow label="Product Name" value={p.product_name} />
                                <DetailRow label="Batch Number" value={p.batch_no} />
                                <DetailRow label="Barcode" value={p.barcode} />
                                <DetailRow label="Category" value={getCategoryLabel(p.category)} />
                                <DetailRow label="Company" value={p.company} />
                                <DetailRow label="Packing Type" value={p.packing_type} />
                                <DetailRow label="Sub Packing Type" value={p.sub_packing_type} />
                                <DetailRow label="Items Per Pack" value={p.items_per_pack} />
                                <DetailRow label="Unit" value={getUnitDisplayLabel(p.unit_of_measure)} />
                                <DetailRow label="Pack Content" value={p.pack_content_label} />
                            </div>
                        </div>

                        {/* Pricing Information */}
                        <div className="ozlistui-view-section">
                            <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faRupeeSign} />Pricing Information</h4>
                            <div className="ozlistui-view-grid">
                                <DetailRow label="MRP" value={toMoney(p.mrp)} />
                                <DetailRow label="Retail Price" value={toMoney(p.sell_per_pack)} />
                                <DetailRow label="MRP per Pack Unit" value={toMoney(p.mrp_per_pack_unit)} />
                                <DetailRow label="MRP per Base Unit" value={toMoney(p.mrp_per_base_unit)} />
                                <DetailRow label="Cost per Pack Unit" value={toMoney(p.cost_price_per_pack_unit)} />
                                <DetailRow label="Cost per Base Unit" value={toMoney(p.cost_price_per_base_unit)} />
                            </div>
                        </div>

                        {/* Stock Information */}
                        <div className="ozlistui-view-section">
                            <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faWarehouse} />Stock Information</h4>
                            <div className="ozlistui-view-grid">
                                <DetailRow label="Total Quantity" value={p.total_quantity || '0'} />
                                <DetailRow label="Actual Stock" value={p.actual_stock_quantity || '0'} />
                                <DetailRow label="Low Stock Alert" value={p.quantity_alert || '0'} />
                                <DetailRow label="Expiry Date" value={formatDate(p.expiry_date)} />
                                <DetailRow label="Date" value={formatDate(p.date)} />
                            </div>
                        </div>

                        {/* Metadata */}
                        <div className="ozlistui-view-section ozlistui-metadata-section">
                            <div className="ozlistui-view-grid">
                                <DetailRow label="Added By" value={<span className="ozlistui-added-by-badge">{p.username || '-'}</span>} />
                                {p.created_at && <DetailRow label="Created At" value={formatDate(p.created_at)} />}
                                {p.updated_at && <DetailRow label="Last Updated" value={formatDate(p.updated_at)} />}
                            </div>
                        </div>
                    </div>
                    <div className="ozlistui-modal-footer">
                        <button className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowViewModal(false)}>Close</button>
                        <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => { setShowViewModal(false); handleEdit(p); }}>
                            <FontAwesomeIcon icon={faEdit} />
                            Edit
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ============================================
    // EDIT MODAL
    // ============================================
    const renderEditModal = () => {
        if (!showEditModal || !selectedProduct) return null;
        return (
            <div className="ozlistui-modal-overlay" onClick={(e) => e.stopPropagation()}>
                <div
                    className="ozlistui-modal-content ozlistui-edit-modal-content"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: 'fixed',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '850px',
                        minWidth: '350px',
                        maxWidth: '95vw',
                        maxHeight: '85vh'
                    }}
                >
                    <div className="ozlistui-modal-header">
                        <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faEdit} /></div>
                        <div className="ozlistui-modal-title">
                            <h3>Edit Product</h3>
                            <span className="ozlistui-product-number-badge">#{selectedProduct.id}</span>
                        </div>
                        <button className="ozlistui-close-btn" onClick={() => setShowEditModal(false)}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    <form onSubmit={handleUpdateProduct}>
                        <div className="ozlistui-modal-body">
                            <div className="ozlistui-form-grid">
                                {/* Product Info */}
                                <div className={`ozlistui-form-group ${editFormErrors.product_name ? 'ozlistui-has-error' : ''}`}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="text"
                                            id="edit_product_name"
                                            name="product_name"
                                            value={editFormData.product_name}
                                            onChange={(e) => setEditFormData({ ...editFormData, product_name: e.target.value })}
                                            placeholder=" "
                                            className={editFormErrors.product_name ? 'ozlistui-error' : ''}
                                        />
                                        <label htmlFor="edit_product_name">
                                            <FontAwesomeIcon icon={faBox} />
                                            Product Name <span className="ozlistui-required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.product_name && (
                                        <div className="ozlistui-error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.product_name}
                                        </div>
                                    )}
                                </div>

                                <div className={`ozlistui-form-group ${editFormErrors.batch_no ? 'ozlistui-has-error' : ''}`}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="text"
                                            id="edit_batch_no"
                                            name="batch_no"
                                            value={editFormData.batch_no}
                                            onChange={(e) => setEditFormData({ ...editFormData, batch_no: e.target.value })}
                                            placeholder=" "
                                            className={editFormErrors.batch_no ? 'ozlistui-error' : ''}
                                        />
                                        <label htmlFor="edit_batch_no">
                                            <FontAwesomeIcon icon={faTag} />
                                            Batch Number <span className="ozlistui-required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.batch_no && (
                                        <div className="ozlistui-error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.batch_no}
                                        </div>
                                    )}
                                </div>

                                <div className="ozlistui-form-group">
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="text"
                                            id="edit_product_code"
                                            name="product_code"
                                            value={editFormData.product_code}
                                            onChange={(e) => setEditFormData({ ...editFormData, product_code: e.target.value })}
                                            placeholder=" "
                                        />
                                        <label htmlFor="edit_product_code">
                                            <FontAwesomeIcon icon={faTag} />
                                            Product Code
                                        </label>
                                    </div>
                                </div>

                                <div className="ozlistui-form-group">
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="text"
                                            id="edit_company"
                                            name="company"
                                            value={editFormData.company}
                                            onChange={(e) => setEditFormData({ ...editFormData, company: e.target.value })}
                                            placeholder=" "
                                        />
                                        <label htmlFor="edit_company">
                                            <FontAwesomeIcon icon={faBuilding} />
                                            Company
                                        </label>
                                    </div>
                                </div>

                                <div className="ozlistui-form-group">
                                    <div className="ozlistui-input-wrapper">
                                        <select
                                            id="edit_category"
                                            name="category"
                                            value={editFormData.category}
                                            onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                                        >
                                            <option value="">Select Category</option>
                                            {categoryOptions.filter(opt => opt.value).map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                        <label htmlFor="edit_category">
                                            <FontAwesomeIcon icon={faTag} />
                                            Category
                                        </label>
                                    </div>
                                </div>

                                {/* Packing */}
                                <div className="ozlistui-form-group">
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="text"
                                            id="edit_packing_type"
                                            name="packing_type"
                                            value={editFormData.packing_type}
                                            onChange={(e) => setEditFormData({ ...editFormData, packing_type: e.target.value })}
                                            placeholder=" "
                                        />
                                        <label htmlFor="edit_packing_type">
                                            <FontAwesomeIcon icon={faBoxes} />
                                            Packing Type
                                        </label>
                                    </div>
                                </div>

                                <div className={`ozlistui-form-group ${editFormErrors.items_per_pack ? 'ozlistui-has-error' : ''}`}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="number"
                                            id="edit_items_per_pack"
                                            name="items_per_pack"
                                            value={editFormData.items_per_pack}
                                            onChange={(e) => setEditFormData({ ...editFormData, items_per_pack: e.target.value })}
                                            step="1"
                                            min="0"
                                            placeholder=" "
                                            className={editFormErrors.items_per_pack ? 'ozlistui-error' : ''}
                                        />
                                        <label htmlFor="edit_items_per_pack">
                                            <FontAwesomeIcon icon={faCalculator} />
                                            Items Per Pack <span className="ozlistui-required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.items_per_pack && (
                                        <div className="ozlistui-error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.items_per_pack}
                                        </div>
                                    )}
                                </div>

                                <div className={`ozlistui-form-group ${editFormErrors.unit_of_measure ? 'ozlistui-has-error' : ''}`}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="text"
                                            id="edit_unit_of_measure"
                                            name="unit_of_measure"
                                            value={editFormData.unit_of_measure}
                                            onChange={(e) => setEditFormData({ ...editFormData, unit_of_measure: e.target.value })}
                                            placeholder=" "
                                            className={editFormErrors.unit_of_measure ? 'ozlistui-error' : ''}
                                        />
                                        <label htmlFor="edit_unit_of_measure">
                                            <FontAwesomeIcon icon={faBalanceScale} />
                                            Unit <span className="ozlistui-required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.unit_of_measure && (
                                        <div className="ozlistui-error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.unit_of_measure}
                                        </div>
                                    )}
                                </div>

                                {/* Pricing */}
                                <div className={`ozlistui-form-group ${editFormErrors.sell_per_pack ? 'ozlistui-has-error' : ''}`}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="number"
                                            id="edit_sell_per_pack"
                                            name="sell_per_pack"
                                            value={editFormData.sell_per_pack}
                                            onChange={(e) => setEditFormData({ ...editFormData, sell_per_pack: e.target.value })}
                                            step="0.01"
                                            min="0"
                                            placeholder=" "
                                            className={editFormErrors.sell_per_pack ? 'ozlistui-error' : ''}
                                        />
                                        <label htmlFor="edit_sell_per_pack">
                                            <FontAwesomeIcon icon={faRupeeSign} />
                                            Retail Price <span className="ozlistui-required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.sell_per_pack && (
                                        <div className="ozlistui-error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.sell_per_pack}
                                        </div>
                                    )}
                                </div>

                                <div className="ozlistui-form-group">
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="number"
                                            id="edit_mrp"
                                            name="mrp"
                                            value={editFormData.mrp}
                                            onChange={(e) => setEditFormData({ ...editFormData, mrp: e.target.value })}
                                            step="0.01"
                                            min="0"
                                            placeholder=" "
                                        />
                                        <label htmlFor="edit_mrp">
                                            <FontAwesomeIcon icon={faMoneyBillWave} />
                                            MRP
                                        </label>
                                    </div>
                                </div>

                                {/* Disposal Information - Highlighted */}
                                <div className="ozlistui-form-group ozlistui-disposal-form-group" style={{ borderColor: '#6b7280' }}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="number"
                                            id="edit_disposed_quantity"
                                            name="disposed_quantity"
                                            value={editFormData.disposed_quantity}
                                            onChange={(e) => setEditFormData({ ...editFormData, disposed_quantity: e.target.value })}
                                            step="0.001"
                                            min="0"
                                            placeholder=" "
                                        />
                                        <label htmlFor="edit_disposed_quantity" style={{ color: '#4b5563' }}>
                                            <FontAwesomeIcon icon={faTrashAlt} style={{ color: '#6b7280' }} />
                                            Disposed Quantity
                                        </label>
                                    </div>
                                </div>

                                <div className="ozlistui-form-group ozlistui-disposal-form-group" style={{ borderColor: '#6b7280' }}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="date"
                                            id="edit_disposal_date"
                                            name="disposal_date"
                                            value={editFormData.disposal_date}
                                            onChange={(e) => setEditFormData({ ...editFormData, disposal_date: e.target.value })}
                                            max={new Date().toISOString().split('T')[0]}
                                            placeholder=" "
                                        />
                                        <label htmlFor="edit_disposal_date" style={{ color: '#4b5563' }}>
                                            <FontAwesomeIcon icon={faCalendar} style={{ color: '#6b7280' }} />
                                            Disposal Date
                                        </label>
                                    </div>
                                </div>

                                <div className="ozlistui-form-group ozlistui-disposal-form-group" style={{ borderColor: '#6b7280' }}>
                                    <div className="ozlistui-input-wrapper">
                                        <select
                                            id="edit_disposal_reason"
                                            name="disposal_reason"
                                            value={editFormData.disposal_reason}
                                            onChange={(e) => setEditFormData({ ...editFormData, disposal_reason: e.target.value })}
                                        >
                                            <option value="">Select Disposal Reason</option>
                                            {disposalReasonOptions.filter(opt => opt.value).map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                        <label htmlFor="edit_disposal_reason" style={{ color: '#4b5563' }}>
                                            <FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#6b7280' }} />
                                            Disposal Reason
                                        </label>
                                    </div>
                                </div>

                                <div className="ozlistui-form-group ozlistui-disposal-form-group" style={{ borderColor: '#6b7280' }}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="text"
                                            id="edit_disposed_by"
                                            name="disposed_by"
                                            value={editFormData.disposed_by || user?.username || ''}
                                            onChange={(e) => setEditFormData({ ...editFormData, disposed_by: e.target.value })}
                                            placeholder=" "
                                        />
                                        <label htmlFor="edit_disposed_by" style={{ color: '#4b5563' }}>
                                            <FontAwesomeIcon icon={faUser} style={{ color: '#6b7280' }} />
                                            Disposed By
                                        </label>
                                    </div>
                                </div>

                                {/* Stock */}
                                <div className={`ozlistui-form-group ${editFormErrors.total_quantity ? 'ozlistui-has-error' : ''}`}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="number"
                                            id="edit_total_quantity"
                                            name="total_quantity"
                                            value={editFormData.total_quantity}
                                            onChange={(e) => setEditFormData({ ...editFormData, total_quantity: e.target.value })}
                                            step="0.001"
                                            min="0"
                                            placeholder=" "
                                            className={editFormErrors.total_quantity ? 'ozlistui-error' : ''}
                                        />
                                        <label htmlFor="edit_total_quantity">
                                            <FontAwesomeIcon icon={faWarehouse} />
                                            Total Quantity <span className="ozlistui-required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.total_quantity && (
                                        <div className="ozlistui-error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.total_quantity}
                                        </div>
                                    )}
                                </div>

                                <div className={`ozlistui-form-group ${editFormErrors.quantity_alert ? 'ozlistui-has-error' : ''}`}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="number"
                                            id="edit_quantity_alert"
                                            name="quantity_alert"
                                            value={editFormData.quantity_alert}
                                            onChange={(e) => setEditFormData({ ...editFormData, quantity_alert: e.target.value })}
                                            step="0.001"
                                            min="0"
                                            placeholder=" "
                                            className={editFormErrors.quantity_alert ? 'ozlistui-error' : ''}
                                        />
                                        <label htmlFor="edit_quantity_alert">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            Low Stock Alert <span className="ozlistui-required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.quantity_alert && (
                                        <div className="ozlistui-error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.quantity_alert}
                                        </div>
                                    )}
                                </div>

                                <div className="ozlistui-form-group">
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="date"
                                            id="edit_expiry_date"
                                            name="expiry_date"
                                            value={editFormData.expiry_date}
                                            onChange={(e) => setEditFormData({ ...editFormData, expiry_date: e.target.value })}
                                            placeholder=" "
                                        />
                                        <label htmlFor="edit_expiry_date">
                                            <FontAwesomeIcon icon={faCalendarAlt} />
                                            Expiry Date
                                        </label>
                                    </div>
                                </div>

                                <div className="ozlistui-form-group">
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="date"
                                            id="edit_date"
                                            name="date"
                                            value={editFormData.date}
                                            onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                                            max={new Date().toISOString().split('T')[0]}
                                            placeholder=" "
                                        />
                                        <label htmlFor="edit_date">
                                            <FontAwesomeIcon icon={faCalendar} />
                                            Date
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="ozlistui-modal-footer">
                            <button type="button" className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowEditModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                                Cancel
                            </button>
                            <button type="submit" className="ozlistui-btn ozlistui-btn-primary" disabled={editLoading}>
                                {editLoading ? (
                                    <>
                                        <div className="ozlistui-btn-spinner"></div>
                                        <span>Updating...</span>
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faSave} />
                                        <span>Save Changes</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    // ============================================
    // DELETE MODAL
    // ============================================
    const renderDeleteModal = () => {
        if (!showDeleteModal) return null;
        return (
            <div className="ozlistui-modal-overlay" onClick={(e) => e.stopPropagation()}>
                <div className="ozlistui-modal-content ozlistui-delete-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="ozlistui-modal-header ozlistui-delete-header">
                        <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
                        <div className="ozlistui-modal-title"><h3>Confirm Delete</h3></div>
                        <button className="ozlistui-close-btn" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
                    </div>
                    <div className="ozlistui-modal-body">
                        <div className="ozlistui-delete-message">
                            <p>Are you sure you want to delete this product?</p>
                            <p><FontAwesomeIcon icon={faExclamationTriangle} /> This action cannot be undone.</p>
                        </div>
                    </div>
                    <div className="ozlistui-modal-footer">
                        <button type="button" className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} />No, Cancel</button>
                        <button type="button" className="ozlistui-btn ozlistui-btn-delete" onClick={handleDelete}><FontAwesomeIcon icon={faTrash} />Yes, Delete</button>
                    </div>
                </div>
            </div>
        );
    };

    // ============================================
    // MAIN RENDER
    // ============================================
    return (
        <div className={`ozlistui-container`}>
            <ToastNotification toast={toast} setToast={setToast} />

            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                loading={loadingShops}
            />

            {renderStickyHeader()}
            {showFilters && renderFiltersSection()}

            {loading ? (
                <div className="ozlistui-loading-container">
                    <div className="ozlistui-loading-spinner"></div>
                    <p>Loading disposed products...</p>
                </div>
            ) : !currentShop ? (
                <div className="ozlistui-no-shop-container">
                    <div className="ozlistui-no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>No Shop Selected</h3>
                        <p>Please select a shop to view disposed products</p>
                        <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => setShowShopSelector(true)}>
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>Switch Shop</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {renderTable()}
                    {renderPaginationControls()}
                </>
            )}

            {renderViewModal()}
            {renderEditModal()}
            {renderDeleteModal()}
        </div>
    );
};

export default PharmacyDisposedProducts;